/**
 * ExtractionLossBanner.tsx — which parts of the Elenco Prezzi we failed to read.
 *
 * WHY THIS EXISTS
 * ---------------
 * `P-52`. The extractor sends the price list to the model in batches. When a
 * batch exceeded the output cap the response was truncated, the loss was
 * swallowed by `except Exception: continue`, and the run reported success. On
 * Omar's TORINO run that discarded items `Nr. 1`–`Nr. 32` of 49. On AOSTA it
 * had been discarding 287 of 482 items on EVERY run since 2026-05-12, and
 * nothing anywhere said so.
 *
 * The damage was never a wrong total — the price list joins to the computo by
 * ID and contributes only the long description, so no item is dropped and no
 * price moves. It is subtler and worse: a missing description makes the
 * regional comparison fall back to the computo's short one, which then cannot
 * match, and the customer is shown «Descrizione difforme dal listino» about
 * their own documents. A silent extraction failure MANUFACTURES discrepancies,
 * and by this project's own rule a checker that cries wolf is how the one real
 * alarm gets ignored.
 *
 * Omar ratified on 2026-08-27 that such a run **completes and produces output,
 * records the loss, and flags it prominently** — not a failed run, and not
 * silence.
 *
 * THE DESIGN RULE, INHERITED FROM ITS THREE SIBLINGS: ABSENCE MUST NEVER READ
 * AS A PASS. So `batchesLost: 0` renders a positive, quiet confirmation rather
 * than nothing — because "we read all of it" is exactly the claim a reader
 * cannot otherwise distinguish from "nobody checked".
 *
 * That rule was broken TWICE by this file's own first draft, and both are worth
 * keeping written down because they are the same mistake wearing two faces.
 *
 *   1. A `null` was described here as having three causes — predates the key,
 *      died early, or a resume — and rendered nothing. The list was copied from
 *      `PriceBookOverlapBanner` WITHOUT the correction that file had already
 *      applied to itself, and so omitted the commonest cause of all: A RUN IN
 *      PROGRESS. `latestJob.result` is null the instant a re-run starts, so the
 *      warning vanished while the previous run's rows sat on screen. `jobStatus`
 *      is why this component takes a third prop.
 *   2. `batchesTotal === 0` — a document that yielded no readable text at all —
 *      satisfied `batchesLost === 0` and printed a GREEN "read in full" over a
 *      run that read nothing.
 *
 * Copying a sibling's shape is right; copying it without its scars is not.
 */
import { type AnalysisJobStatus, type ExtractionLoss } from "../../lib/tenderApi";

type Tone = "ok" | "warn";

const TONE_STYLE: Record<Tone, { fg: string; bg: string; border: string }> = {
  ok: { fg: "var(--c-ok)", bg: "var(--c-ok-soft)", border: "rgba(47,125,78,0.30)" },
  warn: { fg: "var(--c-warn)", bg: "var(--c-warn-soft)", border: "rgba(184,115,12,0.32)" },
};

export interface ExtractionLossCopy {
  tone: Tone;
  title: string;
  detail: string;
}

/**
 * The banner's words, separated from its markup so they can be asserted without
 * rendering — and so the ONE rule that matters here is testable directly: the
 * tone must follow `batchesLost`, never the presence of the object.
 *
 * `tone: "ok"` is emitted ONLY when the run measured zero losses. That is
 * deliberate and it is a lesson paid for: on `P-32` a screen was made to stop
 * showing a verdict by returning the verdict's SUCCESS token, so eight of eight
 * states went green over a run whose audit was failing. A colour is a claim.
 */
export function extractionLossCopy(
  loss: ExtractionLoss | null,
  jobStatus?: AnalysisJobStatus | null,
): ExtractionLossCopy | null {
  if (!loss) {
    /* A NULL HAS FOUR CAUSES and this banner shipped able to name only three.
       Its first docstring listed "predates the key / died early / resume" —
       copied, it turns out, from `PriceBookOverlapBanner`'s accessor comment,
       WITHOUT the correction that file had already made to itself: the
       commonest cause is a RUN IN PROGRESS. `latestJob.result` is null the
       moment a re-run starts, so an amber warning vanished entirely while the
       line items on screen still came from the previous run — beside two
       siblings correctly reporting their figures as stale, and permanent if
       the new run died. Found by the independent tester by READING the sibling,
       not by any mutation: there was no mechanism here to mutate. */
    if (jobStatus === "queued" || jobStatus === "running") {
      return {
        tone: "warn",
        title: "Analisi in corso — elenco prezzi non riletto",
        detail:
          "Quanto mostrato prima sulla lettura dell'elenco prezzi proveniva dall'analisi " +
          "precedente e non è stato riverificato.",
      };
    }
    if (jobStatus === "failed") {
      return {
        tone: "warn",
        title: "Ultima analisi non riuscita — elenco prezzi non riletto",
        detail: "La lettura dell'elenco prezzi si riferisce a un'analisi precedente.",
      };
    }
    return null;
  }

  /* ZERO BLOCKS IS NOT ZERO LOSSES, and conflating them is the defect this
     file exists to fix, relocated one layer up. When the document yields no
     text at all the batch loop never runs, so nothing is ever RECORDED as
     lost — and the naive reading of `batchesLost === 0` printed a green
     «letto per intero» over a run that read NOTHING. Caught in self-review
     before it shipped. It is partly masked downstream (no items means no CSV,
     which usually fails the run later), but "usually crashes first" is not a
     guarantee, and ABSENCE MUST NEVER READ AS A PASS. */
  if (loss.batchesTotal === 0) {
    return {
      tone: "warn",
      title: "Elenco prezzi non letto",
      detail:
        "Dal documento dell'elenco prezzi non è stato estratto alcun contenuto leggibile. " +
        "Le voci del computo non avranno descrizione estesa, e il confronto con il listino " +
        "può quindi segnalare differenze che in realtà non esistono.",
    };
  }

  if (loss.batchesLost === 0) {
    return {
      tone: "ok",
      title: "Elenco prezzi letto per intero",
      detail:
        loss.batchesTotal === null
          ? "Nessun blocco è andato perso durante la lettura del documento."
          : `Tutti i ${loss.batchesTotal} blocchi del documento sono stati letti senza perdite.`,
    };
  }

  const plural = loss.batchesLost === 1 ? "blocco" : "blocchi";
  return {
    tone: "warn",
    title: `Elenco prezzi letto solo in parte — ${loss.batchesLost} ${plural} non ${
      loss.batchesLost === 1 ? "recuperato" : "recuperati"
    }`,
    detail:
      `L'analisi è stata completata, ma ${loss.batchesLost} ${plural}` +
      (loss.batchesTotal === null ? " " : ` su ${loss.batchesTotal} `) +
      "dell'elenco prezzi non sono stati letti. Le voci contenute in quei blocchi non hanno " +
      "descrizione estesa, e il confronto con il listino può quindi segnalare differenze che " +
      "in realtà non esistono. Le voci e gli importi del computo non sono stati alterati.",
  };
}

interface Props {
  loss: ExtractionLoss | null;
  /** False while the analysis has produced no line items — an un-analysed
   * project shows nothing, exactly as its three siblings do. */
  hasAnalysis: boolean;
  /** Status of the job the figures were read from — see `extractionLossCopy`.
   * Without it a re-run silences this banner entirely; all three siblings take
   * it for that reason and this one shipped without it. */
  jobStatus?: AnalysisJobStatus | null;
}

export function ExtractionLossBanner({ loss, hasAnalysis, jobStatus }: Props): JSX.Element | null {
  if (!hasAnalysis) return null;
  const copy = extractionLossCopy(loss, jobStatus);
  if (!copy) return null;
  const tone = TONE_STYLE[copy.tone];
  const lost = loss?.lost ?? [];

  return (
    <div
      role="status"
      data-testid="extraction-loss-banner"
      data-tone={copy.tone}
      style={{
        padding: "11px 14px",
        marginBottom: 16,
        borderRadius: 8,
        background: tone.bg,
        border: `1px solid ${tone.border}`,
      }}
    >
      <div style={{ fontSize: 13, fontWeight: 600, color: tone.fg }}>{copy.title}</div>
      <div
        style={{
          fontSize: 12.5,
          marginTop: 3,
          color: "var(--c-ink-2)",
          lineHeight: 1.5,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {copy.detail}
      </div>

      {lost.length > 0 && (
        /* WHICH blocks, not just how many. Omar's own report of this defect was
           "items Nr. 1-32 of 49" — a range he could act on. A bare count sends
           the reader to a CSV, which is where this failure already hid once. */
        <div style={{ marginTop: 8, overflowX: "auto" }}>
          <table style={{ borderCollapse: "collapse", fontSize: 12, minWidth: 260 }}>
            <thead>
              <tr>
                <th
                  style={{ textAlign: "left", padding: "3px 10px 3px 0", color: "var(--c-ink-2)" }}
                >
                  Blocco
                </th>
                <th style={{ textAlign: "left", padding: "3px 0", color: "var(--c-ink-2)" }}>
                  Motivo
                </th>
              </tr>
            </thead>
            <tbody>
              {lost.map((entry) => (
                <tr key={`${entry.span}-${entry.reason}`}>
                  <td style={{ padding: "3px 10px 3px 0", fontVariantNumeric: "tabular-nums" }}>
                    {entry.span}
                  </td>
                  <td style={{ padding: "3px 0", color: "var(--c-ink-2)" }}>{entry.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
